import type { PaginatedResourceName, ResourcePayload } from "lib/api/resources";

export type PrevPageParams = Record<string, unknown>;

export type PrevPageParamsArgs<R extends PaginatedResourceName> = {
  resourceName: R;
  data: ResourcePayload<R> | undefined;
  currentParams: PrevPageParams | undefined;
};

export type PrevPageParamsGetter<R extends PaginatedResourceName> = (
  args: PrevPageParamsArgs<R>,
) => PrevPageParams | undefined;

function toCamelCase(value: string) {
  return value.replace(/[_-](\w)/g, (_, char: string) => char.toUpperCase());
}

function toPascalCase(value: string) {
  const camelCase = toCamelCase(value);
  return camelCase
    ? camelCase[0].toUpperCase() + camelCase.slice(1)
    : camelCase;
}

function getItemValue(item: Record<string, unknown>, key: string) {
  if (key in item) {
    return item[key];
  }

  const camelCaseKey = toCamelCase(key);
  if (camelCaseKey in item) {
    return item[camelCaseKey];
  }

  const pascalCaseKey = toPascalCase(key);
  if (pascalCaseKey in item) {
    return item[pascalCaseKey];
  }

  return;
}

type CursorKeyAlias = string | Array<string>;
type CursorKeyAliases = Partial<Record<string, CursorKeyAlias>>;

function getPrevPageParamsFromItems<R extends PaginatedResourceName>(
  { data, currentParams }: PrevPageParamsArgs<R>,
  aliases: CursorKeyAliases = {},
) {
  if (
    !data ||
    !currentParams ||
    typeof data !== "object" ||
    !("items" in data)
  ) {
    return;
  }

  const items = (data as { items?: Array<Record<string, unknown>> }).items;
  if (!items?.length) {
    return;
  }

  const cursorKeys = Object.keys(currentParams).filter(
    (key) => key !== "items_count" && key !== "total",
  );
  if (!cursorKeys.length) {
    return;
  }

  const firstItem = items[0];
  const prevParams: PrevPageParams = { ...currentParams };
  let hasCursorValue = false;

  cursorKeys.forEach((key) => {
    const aliasKey = aliases[key];
    const candidates = Array.isArray(aliasKey)
      ? aliasKey
      : aliasKey
        ? [aliasKey]
        : [];
    if (!candidates.includes(key)) {
      candidates.push(key);
    }

    let resolvedValue: unknown;
    for (const candidate of candidates) {
      const value = getItemValue(firstItem, candidate);
      if (value !== undefined) {
        resolvedValue = value;
        break;
      }
    }

    if (resolvedValue !== undefined) {
      const currentValue = currentParams[key];
      prevParams[key] =
        typeof currentValue === "string"
          ? String(resolvedValue)
          : resolvedValue;
      hasCursorValue = true;
    }
  });

  return hasCursorValue ? prevParams : undefined;
}

const getPrevPageParamsByResource: PrevPageParamsGetter<
  PaginatedResourceName
> = (args) => {
  const resourceName = args.resourceName as string;
  if (["general:blocks"].includes(resourceName)) {
    const itemsCount = Number(
      (args.currentParams as Record<string, unknown>)?.items_count,
    );
    const blockNumber = Number(
      (args.currentParams as Record<string, unknown>)?.block_number,
    );
    if (Number.isFinite(itemsCount) && Number.isFinite(blockNumber)) {
      const currentValue = (args.currentParams as Record<string, unknown>)
        ?.block_number;
      return {
        ...args.currentParams,
        block_number:
          typeof currentValue === "string"
            ? String(blockNumber + itemsCount)
            : blockNumber + itemsCount,
      };
    }
  }

  if (
    ["general:l2_withdrawals", "general:optimistic_l2_withdrawals"].includes(
      resourceName,
    )
  ) {
    const itemsCount = Number(
      (args.currentParams as Record<string, unknown>)?.items_count,
    );
    const nonce = Number(
      (args.currentParams as Record<string, unknown>)?.nonce,
    );
    if (Number.isFinite(itemsCount) && Number.isFinite(nonce)) {
      const currentValue = (args.currentParams as Record<string, unknown>)
        ?.nonce;
      return {
        ...args.currentParams,
        nonce:
          typeof currentValue === "string"
            ? String(nonce + itemsCount)
            : nonce + itemsCount,
      };
    }
  }

  if (
    [
      "general:l2_txn_batches",
      "general:zksync_l2_txn_batches",
      "general:zkevm_l2_txn_batches",
      "general:scroll_l2_txn_batches",
      "general:optimistic_l2_txn_batches",
      "general:arbitrum_l2_txn_batches",
    ].includes(resourceName)
  ) {
    const rawItemsCount = Number(
      (args.currentParams as Record<string, unknown>)?.items_count,
    );
    const itemsCount =
      Number.isFinite(rawItemsCount) && rawItemsCount > 0 && rawItemsCount <= 50
        ? rawItemsCount
        : 50;
    const batchIndex = Number(
      (args.currentParams as Record<string, unknown>)?.batch_index,
    );
    if (Number.isFinite(itemsCount) && Number.isFinite(batchIndex)) {
      const currentValue = (args.currentParams as Record<string, unknown>)
        ?.batch_index;
      return {
        ...args.currentParams,
        batch_index:
          typeof currentValue === "string"
            ? String(batchIndex + itemsCount)
            : batchIndex + itemsCount,
      };
    }
  }

  const aliasesByResource: Record<string, CursorKeyAliases> = {
    "general:l2_withdrawals": {
      nonce: ["withdrawalNonce", "nonce", "msg_nonce"],
    },
    "general:optimistic_l2_withdrawals": { nonce: "msg_nonce" },
    "general:l2_deposits": {
      l1_block_number: ["blockNumber", "l1_block_number"],
      tx_hash: ["transactionHash", "tx_hash", "l1_transaction_hash"],
    },
    "general:optimistic_l2_deposits": {
      transaction_hash: "l1_transaction_hash",
    },
    "general:shibarium_deposits": { block_number: "l1_block_number" },
    "general:shibarium_withdrawals": { block_number: "l2_block_number" },
    "general:references": {
      latest_seen_at: ["latest_seen_at", "timestamp"],
      reference_key: ["reference", "reference_key"],
    },
    "general:reference": { index: ["position", "index"] },
  };

  return getPrevPageParamsFromItems(args, aliasesByResource[resourceName]);
};

export default getPrevPageParamsByResource;
